import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Panel from '@hobs/components/Panel';
import CheckboxTree from '@hobs/components/CheckboxTree';
import Confirm from '@hobs/components/Confirm';
import InfoPanel from '@hobs/components/InfoPanel';
import File from '@hobs/components/File';
import Textarea from '@hobs/components/Textarea';
import DropZone from '@hobs/components/DropZone';
import PageActions from '@hobs/components/PageActions';
import ShortId from 'shortid';
import objectPath from 'object-path';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Input from '@hobs/components/Input';
import PageHeader from '@hobs/components/PageHeader';
import { H2, H5 } from '@hobs/components/Headings';
import { countDecimals } from '@hobs/utils/countDecimals';
import { Formik, Field, FieldArray } from 'formik';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import GridLoader from '@hobs/components/Grid/GridLoader';
import Toast from '@hobs/components/Toast';
import * as Yup from 'yup';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import { SERVICE_REQUEST_NAVIGATION_ORIGIN } from '@hobs/utils/constants';
import CalculateByDays from '@hobs/utils/calculateByDays';
import DeliveryInformation from '../../components/DeliveryInformation';
import LocationDetails from '../../components/LocationDetails';

import './ServiceRequest.scss';
import Money from '@hobs/components/Money';
import Tooltip from '@hobs/components/Tooltip/Tooltip';
import DateInput from '@hobs/components/DateInput';
import Dropdown from '@hobs/components/Dropdown';
import NumericInput from '@hobs/components/NumericInput';

class ServiceRequest extends PureComponent {
  state = {
    chTreeChecked: [],
    chTreeExpanded: [],
    closeForm: false,
    selectedCatalog: {},
    oDataAnnexesFilter: {},
    oDataSearchFilter: {},
    dataState: {
      take: 17,
      skip: 0,
      filter: {},
      sort: [{ field: 'Name', dir: 'asc' }]
    },
    isFirstPanelExpanded: true,
    isTransportationWindowOpen: false,
    prevFormData: {
      unitId: '',
      dateFrom: null,
      dateTo: null,
      isCalculatedByDays: false,

      location: null,
      regionId: null,
      regionName: null,
      locationName: null,
      locationComment: '',
      site: null,
      latitude: null,
      longitude: null,

      locationFrom: null,
      regionIdFrom: null,
      regionNameFrom: null,
      locationNameFrom: null,
      locationCommentFrom: '',
      siteFrom: null,
      latitudeFrom: null,
      longitudeFrom: null
    }
  };

  constructor(props) {
    super(props);
    const { t } = props;
    this.handleQuickSearchChangeTimeout = null;
    this.locationQueryTimeout = null;
    this.form = React.createRef();

    const getValue = (value, eq) => {
      return Boolean(value && value.toString() && value.toString() === eq);
    };

    this.validationSchema = Yup.object().shape({
      unitId: Yup.string().required(t('Unit is required')),
      dateFrom: Yup.date()
        .required(t('From date is required'))
        .typeError(t('Invalid date or date format'))
        .nullable(),
      quantity: Yup.number()
        .min(0, t('Quantity must be greater than 0'))
        .required(t('Quantity is required'))
        .typeError(t('Invalid quantity format')),
      location: Yup.object().when(['isFsorPhaseOrLater'], {
        is: true,
        then: Yup.object()
          .required(t('Location is required'))
          .nullable(),
        otherwise: Yup.object().nullable()
      }),
      locationFrom: Yup.object().when(
        ['isFsorPhaseOrLater', 'isTransportation'],
        {
          is: true,
          then: Yup.object()
            .required(t('Location is required'))
            .nullable(),
          otherwise: Yup.object().nullable()
        }
      ),
      comment: Yup.string()
        .max(1500, t('Comment cannot be longer than 1500 symbols'))
        .nullable(),
      generalInformation: Yup.object().shape({
        serialNumber: Yup.string()
          .nullable()
          .max(15, t('Serial number cannot be longer than 15 symbols'))
      }),
      pickupDetails: Yup.object().when(
        ['isAdditionalRequest', 'isTransportation'],
        {
          is: true,
          then: Yup.object({
            namePoc: Yup.string()
              .nullable()
              .max(256, t('Name POC cannot be longer than 256 symbols'))
              .required(t('Name POC is required')),
            location: Yup.object().shape({
              name: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Place of loading cannot be longer than 256 symbols')
                )
                .required(t('Place of loading is required')),
              zipCode: Yup.string()
                .nullable()
                .max(5, t('Zip code cannot be longer than 5 symbols')),
              postAddress: Yup.string()
                .nullable()
                .max(50, t('Post. add. cannot be longer than 50 symbols')),
              comment: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Location comment cannot be longer than 256 symbols')
                )
            }),
            mobilePhone: Yup.string()
              .nullable()
              .required(t('Mobile phone is required')),
            email: Yup.string()
              .nullable()
              .email(t('Invalid email format'))
              .required(t('Email is required')),
            mission: Yup.string()
              .nullable()
              .max(256, t('Mission cannot be longer than 256 symbols'))
          }),
          otherwise: Yup.object({
            namePoc: Yup.string()
              .nullable()
              .max(256, t('Name POC cannot be longer than 256 symbols')),
            location: Yup.object().shape({
              name: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Place of loading cannot be longer than 256 symbols')
                ),
              zipCode: Yup.string()
                .nullable()
                .max(5, t('Zip code cannot be longer than 5 symbols')),
              postAddress: Yup.string()
                .nullable()
                .max(50, t('Post. add. cannot be longer than 50 symbols')),
              comment: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Location comment cannot be longer than 256 symbols')
                )
            }),
            mobilePhone: Yup.string().nullable(),
            email: Yup.string()
              .nullable()
              .email(t('Invalid email format')),
            mission: Yup.string()
              .nullable()
              .max(256, t('Mission cannot be longer than 256 symbols'))
          })
        }
      ),
      deliveryDetails: Yup.object().when(
        ['isAdditionalRequest', 'isTransportation'],
        {
          is: true,
          then: Yup.object({
            namePoc: Yup.string()
              .nullable()
              .max(256, t('Name POC cannot be longer than 256 symbols'))
              .required(t('Name POC is required')),
            location: Yup.object().shape({
              name: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Place of loading cannot be longer than 256 symbols')
                )
                .required(t('Place of loading is required')),
              zipCode: Yup.string()
                .nullable()
                .max(5, t('Zip code cannot be longer than 5 symbols')),
              postAddress: Yup.string()
                .nullable()
                .max(50, t('Post. add. cannot be longer than 50 symbols')),
              comment: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Location comment cannot be longer than 256 symbols')
                )
            }),
            mobilePhone: Yup.string()
              .nullable()
              .required(t('Mobile phone is required')),
            email: Yup.string()
              .nullable()
              .email(t('Invalid email format'))
              .required(t('Email is required')),
            mission: Yup.string()
              .nullable()
              .max(256, t('Mission cannot be longer than 256 symbols'))
          }),
          otherwise: Yup.object({
            namePoc: Yup.string()
              .nullable()
              .max(256, t('Name POC cannot be longer than 256 symbols')),
            location: Yup.object().shape({
              name: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Place of loading cannot be longer than 256 symbols')
                ),
              zipCode: Yup.string()
                .nullable()
                .max(5, t('Zip code cannot be longer than 5 symbols')),
              postAddress: Yup.string()
                .nullable()
                .max(50, t('Post. add. cannot be longer than 50 symbols')),
              comment: Yup.string()
                .nullable()
                .max(
                  256,
                  t('Location comment cannot be longer than 256 symbols')
                )
            }),
            mobilePhone: Yup.string().nullable(),
            email: Yup.string()
              .nullable()
              .email(t('Invalid email format')),
            mission: Yup.string()
              .nullable()
              .max(256, t('Mission cannot be longer than 256 symbols'))
          })
        }
      ),
      lines: Yup.array().of(
        Yup.object().shape({
          typeId: Yup.string()
            .nullable()
            .required(t('Item type is required'))
            .typeError(t('Item type is required')),
          description: Yup.string()
            .nullable()
            .max(30, t('Description cannot be longer than 30 symbols'))
            .required(t('Description is required')),
          quantity: Yup.number()
            .nullable()
            .min(1, t('Quantity must be greater than 0'))
            .required(t('Quantity is required'))
            .typeError(t('Quantity is required')),
          length: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid length format'))
                  .nullable()
                  .min(0.01, t('Length must be greater than 0'))
                  .test('format', t('Invalid length format'), val => {
                    return countDecimals(val) < 3;
                  })
              : Yup.number(t('Invalid length format'))
                  .test('format', t('Invalid length format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Length must be greater than 0'))
                  .required(t('Length is required'))
                  .typeError(t('Invalid length format'))
          ),
          width: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid width format'))
                  .test('format', t('Invalid width format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Width must be greater than 0'))
              : Yup.number(t('Invalid width format'))
                  .test('format', t('Invalid width format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Width must be greater than 0'))
                  .required(t('Width is required'))
                  .typeError(t('Invalid width format'))
          ),
          height: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid height format'))
                  .test('format', t('Invalid height format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Height must be greater than 0'))
              : Yup.number(t('Invalid height format'))
                  .nullable()
                  .test('format', t('Invalid height format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .min(0.01, t('Height must be greater than 0'))
                  .required(t('Height is required'))
                  .typeError(t('Invalid height format'))
          ),
          weight: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid weight format'))
                  .test('format', t('Invalid weight format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Weight must be greater than 0'))
              : Yup.number(t('Invalid weight format'))
                  .test('format', t('Invalid weight format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Weight must be greater than 0'))
                  .required(t('Weight is required'))
                  .typeError(t('Invalid weight format'))
          ),
          dgrName: Yup.string().when('typeId', value =>
            getValue(value, '2') || getValue(value, '3')
              ? Yup.string()
                  .nullable()
                  .max(30, t('DGR name cannot be longer than 30 symbols'))
              : Yup.string()
                  .nullable()
                  .max(30, t('DGR name cannot be longer than 30 symbols'))
                  .required(t('DGR name is required'))
          ),
          unNr: Yup.string().when('typeId', value =>
            getValue(value, '2') || getValue(value, '3')
              ? Yup.string().nullable()
              : Yup.string()
                  .nullable()
                  .test(
                    'format',
                    t('Invalid UN-NR format'),
                    val => val && val.toString().length === 4
                  )
                  .required(t('UN-NR is required'))
          ),
          dgrClass: Yup.number().when('typeId', value =>
            getValue(value, '2') || getValue(value, '3')
              ? Yup.number().nullable()
              : Yup.number()
                  .typeError(t('DGR class is required'))
                  .nullable()
                  .test('format', t('Invalid DGR class format'), val => {
                    return val > 1 && val < 10 && countDecimals(val) < 3;
                  })
                  .required(t('DGR class is required'))
          ),
          neq: Yup.string().when('typeId', value =>
            !getValue(value, '2') && !getValue(value, '3')
              ? Yup.number(t('Invalid NEQ format'))
                  .nullable()
                  .test('format', t('Invalid NEQ format'), val => {
                    return val >= 0 && countDecimals(val) < 3;
                  })
                  .min(0, t('NEQ cannot be less than 0'))
                  .required(t('NEQ is required'))
                  .typeError(t('Invalid NEQ format'))
              : Yup.number().nullable()
          ),
          remarks: Yup.string()
            .nullable()
            .max(50, t('Remarks cannot be longer than 50 symbols'))
        })
      )
    });
  }

  componentDidMount() {
    this.loadRequiredData();
    this.props.clearAttachments();
  }

  loadRequiredData() {
    this.props.forSubmit(this.props.match.params.partyId);
    this.props.getAnnexesTree();
    this.queryOdata();
    this.props.forTransportationSubmit();
  }

  componentWillUnmount() {
    this.props.resetOData();
    this.props.clearAttachments();
    this.props.clearCalculateByDays();
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  getServiceRequestsGridUrl(navContext) {
    if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets) {
      return '/tickets';
    }
    return `/service-requests/${this.props.match.params.partyId}`;
  }

  getCatalogItemsQueryUrl() {
    return `CatalogItemsQueryForServiceRequest(${this.props.match.params.partyId})`;
  }

  getCustomServiceRequestUrl() {
    return `/custom-service-request/${this.props.match.params.partyId}`;
  }

  isAdditionalRequest() {
    const { forSubmitData } = this.props;
    return forSubmitData.isAdditionalSrCreateForm;
  }

  isFsorPhaseOrLater() {
    const { forSubmitData } = this.props;
    return forSubmitData.isFsorPhaseOrLater;
  }

  _create(data) {
    const { create, createAdditionalServiceRequest } = this.props;
    return this.isAdditionalRequest()
      ? createAdditionalServiceRequest(data)
      : create(data);
  }

  getServiceRequestEditUrl(serviceRequestId) {
    const { match } = this.props;

    return `/service-request/${match.params.partyId}/${serviceRequestId}`;
  }

  closeCalcByDaysTooltip() {
    const isTooltipOpen = document.getElementsByClassName('bp3-tooltip').length;

    if (isTooltipOpen) {
      const tooltipIcon = document.getElementById('tooltip-icon');
      if (tooltipIcon) {
        tooltipIcon.click();
      }
    }
  }

  onSubmit = (values, { setErrors, setFieldValue, setSubmitting }) => {
    const { selectedCatalog, closeForm } = this.state;
    const { forSubmitData, t, history } = this.props;

    const submitData = {
      catalogItemId: selectedCatalog.CatalogItemId,
      unitOfMeasureId:
        values.unitOfMeasureId === ''
          ? selectedCatalog.UnitsOfMeasure[0].UnitOfMeasureId
          : values.unitOfMeasureId,
      statementOfRequirementId: forSubmitData.statementOfRequirementId,
      unitId: values.unitId,
      activityId: values.activityId,
      requestedQuantity: values.quantity,
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      isCalculatedByDays: values.isCalculatedByDays,
      regionId: objectPath.get(
        values.location,
        'original.region.regionId',
        null
      ),
      regionName: objectPath.get(values.location, 'original.region.name', null),
      locationName: objectPath.get(values.location, 'original.name', null),
      locationComment: values.locationComment,
      site: objectPath.get(values.location, 'original.site', null),
      latitude: objectPath.get(values.location, 'original.latitude', null),
      longitude: objectPath.get(values.location, 'original.longtitude', null),

      // from
      regionIdFrom: objectPath.get(
        values.locationFrom,
        'original.region.regionId',
        null
      ),
      regionNameFrom: objectPath.get(
        values.locationFrom,
        'original.region.name',
        null
      ),
      locationNameFrom: objectPath.get(
        values.locationFrom,
        'original.name',
        null
      ),
      locationCommentFrom: values.locationCommentFrom,
      siteFrom: objectPath.get(values.locationFrom, 'original.site', null),
      latitudeFrom: objectPath.get(
        values.locationFrom,
        'original.latitude',
        null
      ),
      longitudeFrom: objectPath.get(
        values.locationFrom,
        'original.longtitude',
        null
      ),
      comment: values.comment,
      attachments: this.props.attachments.map(
        attachment => attachment.attachmentId
      ),

      // transportation
      generalInformation: values.generalInformation,
      lines: values.lines,
      pickupDetails: values.pickupDetails,
      deliveryDetails: values.deliveryDetails,
      transportInformation: values.transportInformation
    };

    this._create(submitData).then(response => {
      if (response.success) {
        Toast.success(
          this.isAdditionalRequest()
            ? t('Additional service request submitted successfully')
            : t('Service request was successfully created')
        );

        if (closeForm) {
          if (this.isAdditionalRequest()) {
            history.push(
              this.getServiceRequestEditUrl(response.payload.serviceRequestId)
            );
          } else {
            history.push(this.getServiceRequestsGridUrl());
          }
        } else {
          setFieldValue('quantity', '', false);
          setFieldValue('unitOfMeasureId', '', false);
          setFieldValue('comment', '', false);
          this.props.clearAttachments();
          this.setState({
            attachments: [],
            selectedCatalog: {},
            prevFormData: {
              ...submitData,
              location: values.location,
              locationFrom: values.locationFrom
            }
          });
        }
      } else {
        Toast.warning(t(response.payload.message));
        const errors = (response.payload.details || []).reduce(
          (results, error) => {
            return {
              ...results,
              [error.target]: error.message[0]
            };
          },
          {}
        );

        setErrors(errors);
        this.setState({
          closeForm: false
        });
      }

      setSubmitting(false);
    });

    this.closeCalcByDaysTooltip();
  };

  toAnnexesTreeEntity() {
    return this.props.annexesTree.map(annex => {
      return {
        value: annex.annexId,
        label: annex.name,
        children: annex.groups.map(group => {
          return {
            value: group.groupId,
            label: group.name
          };
        })
      };
    });
  }

  clearAnnexesTree = event => {
    event.preventDefault();
    this.setState(
      {
        chTreeChecked: [],
        chTreeExpanded: [],
        oDataAnnexesFilter: {}
      },
      () => {
        this.queryOdata();
      }
    );
  };

  onAnnexCheck = checked => {
    let filter = {};

    if (checked.length > 0) {
      filter = {
        logic: 'or',
        filters: checked.map(item => {
          return {
            field: 'GroupId',
            operator: 'eq',
            value: `<guid>${item}</guid>`
          };
        })
      };
    }

    this.setState(
      {
        dataState: {
          ...this.state.dataState,
          skip: 0
        },
        oDataAnnexesFilter: filter,
        chTreeChecked: checked
      },
      () => {
        this.queryOdata();
      }
    );
  };

  queryOdata() {
    const { oDataSearchFilter, oDataAnnexesFilter, dataState } = this.state;

    const filters = [];

    const params = {
      ...dataState
    };

    if (Object.keys(oDataSearchFilter).length > 0) {
      filters.push(oDataSearchFilter);
    }
    if (Object.keys(oDataAnnexesFilter).length > 0) {
      filters.push(oDataAnnexesFilter);
    }

    if (filters.length > 0) {
      params.filter = {
        logic: 'and',
        filters
      };
    }

    this.props.queryOData(this.getCatalogItemsQueryUrl(), params);
  }

  dataStateChange = e => {
    this.setState(
      {
        dataState: e.dataState
      },
      () => {
        this.queryOdata();
      }
    );
  };

  onLocationQueryChange = query => {
    if (this.locationQueryTimeout) {
      clearTimeout(this.locationQueryTimeout);
    }

    this.locationQueryTimeout = setTimeout(() => {
      this.props.queryLocations(query);
    }, 300);
  };

  toAutocompleteEntity(data) {
    return data.map(item => ({
      value: item.code,
      title: item.name,
      original: item
    }));
  }

  calculateByDays(values) {
    const { selectedCatalog } = this.state;
    if (selectedCatalog) {
      values.isAccommodation = selectedCatalog.IsAccommodation;
    }
    const data = CalculateByDays.calculate(values, selectedCatalog);

    if (data) {
      this.props.calculateByDays(data);
    }
  }

  toUnitsEntity(units) {
    return units.map(unit => {
      return {
        title: unit.title,
        value: unit.unitId
      };
    });
  }

  toActivityEntity(activities) {
    return activities.map(activity => {
      return {
        name: activity.name,
        activityId: activity.activityId,
        isDefault: activity.isDefault
      };
    });
  }

  toUomsEntity(uoms) {
    return uoms.map(uom => {
      return {
        title: uom.UnitOfMeasureCode,
        value: uom.UnitOfMeasureId
      };
    });
  }

  validation = values => {
    this.calculateByDays(values);
  };

  handleQuickSearchChange = event => {
    if (this.handleQuickSearchChangeTimeout) {
      clearTimeout(this.handleQuickSearchChangeTimeout);
    }
    const value = event.target.value;

    this.handleQuickSearchChangeTimeout = setTimeout(() => {
      let state = {};

      if (value === '') {
        state = {
          oDataSearchFilter: {}
        };
      } else {
        state = {
          dataState: {
            ...this.state.dataState,
            skip: 0
          },
          oDataSearchFilter: {
            logic: 'and',
            filters: [
              {
                field: 'QuickSearchField',
                operator: 'contains',
                value: value
              }
            ]
          }
        };
      }

      this.setState(state, () => {
        this.queryOdata();
      });
    }, 100);
  };

  checkAttachmentFileNames = fileName => {
    const { attachments } = this.state;
    return (
      Object.values(attachments).find(item => {
        return item.name === fileName;
      }) || false
    );
  };

  isTransportation = () => {
    return (
      this.state.selectedCatalog &&
      this.state.selectedCatalog.IsTransportation === true
    );
  };

  isAccommodation = () => {
    return (
      this.state.selectedCatalog &&
      this.state.selectedCatalog.IsAccommodation === true
    );
  };

  isMeal = () => {
    return (
      this.state.selectedCatalog && this.state.selectedCatalog.IsMeal === true
    );
  };

  isAccommodationOrMeal = () => {
    return this.isAccommodation() || this.isMeal();
  };

  getInitialActivity = () => {
    if (this.state.serviceRequest && this.state.serviceRequest.activityId) {
      return this.state.serviceRequest.activityId;
    }

    if (
      this.props.forSubmitData.activities &&
      this.props.forSubmitData.activities.length > 0 &&
      !this.state.disabled &&
      this.toActivityEntity(this.props.forSubmitData.activities).some(
        x => x.isDefault
      )
    ) {
      return this.toActivityEntity(this.props.forSubmitData.activities).find(
        x => x.isDefault
      ).activityId;
    }

    return null;
  };

  cancelForm(dirty, navContext) {
    if (!dirty) {
      this.props.history.push(this.getServiceRequestsGridUrl(navContext));
    } else {
      Confirm(
        this.props.t('Cancel service request'),
        this.props.t(
          'Are you sure you want to cancel? All entered data will be lost.'
        ),
        () => {
          this.props.history.push(this.getServiceRequestsGridUrl(navContext));
        }
      );
    }
  }

  getUnits = units => {
    const { serviceRequest } = this.state;
    const newUnits = this.flattenUnitsTree(units).map(unit => {
      return {
        title: unit.unitName,
        unitId: unit.unitId
      };
    });
    if (
      serviceRequest &&
      !newUnits.some(unit => unit.unitId === serviceRequest.unitId)
    ) {
      newUnits.push({
        title: serviceRequest.unitName,
        unitId: serviceRequest.unitId
      });
    }

    return this.toUnitsEntity(newUnits);
  };

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    const units = array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.unitName}`,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);

    return units;
  }

  onGridRowClick = (dataItem, setFieldValue, validateForm) => {
    const { prevFormData } = this.state;
    this.setState(
      {
        selectedCatalog: dataItem
      },
      () => {
        const uom =
          objectPath
            .get(dataItem, 'UnitsOfMeasure', [])
            .find(uom => uom.IsDefault) || {};

        setFieldValue('isAdditionalRequest', this.isAdditionalRequest());
        setFieldValue('isTransportation', this.isTransportation());
        setFieldValue('isAccommodation', this.isAccommodation());
        setFieldValue('isFsorPhaseOrLater', this.isFsorPhaseOrLater());
        if (uom) {
          setFieldValue('unitOfMeasureId', uom.UnitOfMeasureId);
        }
        // check to see if form was filled before
        // only set calc by days if form is new
        if (!prevFormData.unitId) {
          setFieldValue('isCalculatedByDays', this.isAccommodationOrMeal());
        }
        validateForm();
      }
    );
  };

  toggleFirstPanelExpanded = () => {
    this.setState({
      isFirstPanelExpanded: !this.state.isFirstPanelExpanded
    });
  };

  getExerciseCurrency = () => {
    if (this.props.forSubmitData.exerciseCurrency) {
      return this.props.forSubmitData.exerciseCurrency.name;
    }

    return '';
  };

  renderGeneralInformation() {
    const { t } = this.props;

    return (
      <Panel>
        <div className="transportation--panel">
          <H2>{t('General information')}</H2>

          <div className="transportation--fields-row">
            <div className="transportation--field">
              <Field
                component={Input}
                placeholder={t('Serial number')}
                name="generalInformation.serialNumber"
              >
                {t('Serial number')}
              </Field>
            </div>
            <div className="transportation--field">
              <Field
                component={Input}
                placeholder={t('PO number')}
                name="generalInformation.poNumber"
              >
                {t('PO number')}
              </Field>
            </div>
          </div>
        </div>
      </Panel>
    );
  }

  renderTransportationInfo(values) {
    const { t, forTransportationSubmitData } = this.props;

    return (
      <div className="transportation--fields-row">
        <div className="transportation--field">
          <Field
            component={Dropdown}
            name="transportInformation.vehicleTypeId"
            label={t('Vehicle type')}
            disabled={forTransportationSubmitData.vehicleTypes.length === 0}
          >
            <option value="">{t('Please select')}</option>
            {forTransportationSubmitData.vehicleTypes.map(item => {
              return (
                <option key={item.vehicleTypeId} value={item.vehicleTypeId}>
                  {item.vehicleType}
                </option>
              );
            })}
          </Field>
        </div>
        <div className="transportation--field">
          <Field
            component={Dropdown}
            name="transportInformation.transportTypeId"
            label={t('Transportation type')}
            disabled={forTransportationSubmitData.transportTypes.length === 0}
          >
            <option value="">{t('Please select')}</option>
            {forTransportationSubmitData.transportTypes.map(item => {
              return (
                <option
                  key={item.transportationTypeId}
                  value={item.transportationTypeId}
                >
                  {item.transportationType}
                </option>
              );
            })}
          </Field>
        </div>
        <div className="transportation--field">
          <Field
            component={Dropdown}
            name="transportInformation.supplierName"
            label={t('Supplier')}
            disabled={forTransportationSubmitData.suppliers.length === 0}
          >
            {forTransportationSubmitData.suppliers.length === 0 && (
              <option value="">{t('There are no subcontractors')}</option>
            )}

            {values.transportInformation.supplierName !== '' &&
              !forTransportationSubmitData.suppliers.find(
                item =>
                  item.supplierName === values.transportInformation.supplierName
              ) && (
                <option value={values.transportInformation.supplierName}>
                  {values.transportInformation.supplierName}
                </option>
              )}
            <option value="">{t('Please select')}</option>
            {forTransportationSubmitData.suppliers.map(item => {
              return (
                <option key={item.supplierId} value={item.supplierName}>
                  {item.supplierName}
                </option>
              );
            })}
          </Field>
        </div>
      </div>
    );
  }

  timeCheck = (minutes = false) => event => {
    const {
      target: { value, selectionStart },
      charCode
    } = event;

    const allowedFirstNumber = minutes
      ? selectionStart === 0 &&
        [48, 49, 50, 51, 52, 53].indexOf(charCode) === -1
      : [48, 49, 50].indexOf(charCode) === -1 && selectionStart === 0;

    const allowedSecondNumber = minutes
      ? false
      : selectionStart === 1 &&
        (parseInt(value, 10) === 2 &&
          [48, 49, 50, 51].indexOf(charCode) === -1);

    if (
      [48, 49, 50, 51, 52, 53, 54, 55, 56, 57].indexOf(charCode) === -1 ||
      value > 59 ||
      allowedFirstNumber ||
      allowedSecondNumber ||
      value.length === 2
    ) {
      event.preventDefault();
    }
  };

  convertObjectNullsToString(object) {
    return Object.keys(object).reduce((result, item) => {
      if (result[item] === null) {
        result[item] = '';
      }

      return result;
    }, object);
  }

  getPaddedTime(time) {
    if (time != null && time < 10 && (!time.length || time.length <= 1)) {
      return `0${time}`;
    }
    return time;
  }

  setTransportationTime(transportationTime) {
    if (transportationTime) {
      transportationTime.hour = this.getPaddedTime(transportationTime.hour);
      transportationTime.minute = this.getPaddedTime(transportationTime.minute);
    }
    return transportationTime;
  }

  getInitialValues() {
    const { prevFormData } = this.state;

    return {
      isAdditionalRequest: this.isAdditionalRequest(),
      isTransportation: false,
      isAccommodation: false,
      isMeal: false,
      quantity: '',
      dateFrom: prevFormData.dateFrom,
      dateTo: prevFormData.dateTo,
      isCalculatedByDays: prevFormData.isCalculatedByDays,

      catalogItemId: null,
      unitOfMeasureId: '',
      statementOfRequirementId: null,
      unitId: prevFormData.unitId,

      // locations
      location: prevFormData.location,
      regionId: prevFormData.regionId,
      regionName: prevFormData.regionName,
      locationName: prevFormData.locationName,
      locationComment: prevFormData.locationComment,
      site: prevFormData.site,
      latitude: prevFormData.latitude,
      longitude: prevFormData.longitude,

      // locations from
      locationFrom: this.isTransportation() ? prevFormData.locationFrom : null,
      regionIdFrom: this.isTransportation() ? prevFormData.regionIdFrom : null,
      regionNameFrom: this.isTransportation()
        ? prevFormData.regionNameFrom
        : null,
      locationNameFrom: this.isTransportation()
        ? prevFormData.locationNameFrom
        : null,
      locationCommentFrom: this.isTransportation()
        ? prevFormData.locationCommentFrom
        : '',
      siteFrom: this.isTransportation() ? prevFormData.siteFrom : null,
      latitudeFrom: this.isTransportation() ? prevFormData.latitudeFrom : null,
      longitudeFrom: this.isTransportation()
        ? prevFormData.longitudeFrom
        : null,

      // additional details
      activityId: this.getInitialActivity(),
      comment: '',
      attachments: [],

      //transportation details
      generalInformation: {
        serialNumber: '',
        poNumber: ''
      },
      pickupDetails: {
        email: '',
        location: {
          comment: '',
          latitude: '',
          longitude: '',
          name: '',
          postAddress: '',
          zipCode: ''
        },
        mission: '',
        mobilePhone: '',
        namePoc: '',
        phone: '',
        transportationTime: {
          date: '',
          hour: '',
          minute: ''
        }
      },
      deliveryDetails: {
        email: '',
        location: {
          comment: '',
          latitude: '',
          longitude: '',
          name: '',
          postAddress: '',
          zipCode: ''
        },
        mission: '',
        mobilePhone: '',
        namePoc: '',
        phone: '',
        transportationTime: {
          date: '',
          hour: '',
          minute: ''
        }
      },
      transportInformation: {
        vehicleType: '',
        vehicleTypeId: '',
        transportType: '',
        transportTypeId: '',
        supplierName: '',
        supplierId: ''
      },
      lines: []
    };
  }

  getInitialLocation(location) {
    return {
      original: {
        latitude: location ? location.original.latitude : '',
        longtitude: location ? location.original.longtitude : '',
        site: location ? location.original.site : '',
        name: location ? location.original.name : '',
        region: {
          name:
            location && location.original.region
              ? location.original.region.name
              : ''
        }
      }
    };
  }

  render() {
    const { t, forTransportationSubmitData } = this.props;
    const {
      selectedCatalog,
      isFirstPanelExpanded,
      isTransportationWindowOpen,
      prevFormData
    } = this.state;

    const defaultUom =
      objectPath
        .get(selectedCatalog, 'UnitsOfMeasure', [])
        .find(uom => uom.IsDefault) || {};
    const isDisabled = Object.keys(selectedCatalog).length === 0;

    return (
      <Formik
        validateOnBlur={false}
        validateOnChange={true}
        validate={this.validation}
        enableReinitialize={true}
        validationSchema={this.validationSchema}
        initialValues={this.getInitialValues()}
        onSubmit={this.onSubmit}
      >
        {({
          setFieldValue,
          handleReset,
          handleSubmit,
          isSubmitting,
          validateForm,
          values,
          dirty,
          isValid
        }) => (
          <form onReset={handleReset} onSubmit={handleSubmit} ref={this.form}>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <>
                  <PageActions container={this.form.current}>
                    <div />
                    <div>
                      <GhostButton
                        onClick={() =>
                          this.cancelForm(dirty, nav.serviceRequestNavigation)
                        }
                        large
                      >
                        {t('Cancel')}
                      </GhostButton>
                      <RaisedButton
                        disabled={isDisabled || isSubmitting}
                        onClick={() => {
                          if (
                            this.isAdditionalRequest() &&
                            this.isTransportation()
                          ) {
                            validateForm().then(result => {
                              let requiredFieldsValid = true;
                              const requiredFieldsBeforeTransportation = [
                                'unitId',
                                'dateFrom',
                                'quantity',
                                'locationFrom',
                                'location'
                              ];
                              Object.keys(result).forEach(function(key, index) {
                                if (
                                  requiredFieldsBeforeTransportation.includes(
                                    key
                                  )
                                ) {
                                  requiredFieldsValid = false;
                                }
                              });
                              if (requiredFieldsValid) {
                                this.setState({
                                  isTransportationWindowOpen: true
                                });

                                setFieldValue(
                                  'pickupDetails.location.name',
                                  values.locationFrom.original.name
                                );
                                setFieldValue(
                                  'pickupDetails.location.latitude',
                                  values.locationFrom.original.latitude
                                );
                                setFieldValue(
                                  'pickupDetails.location.longitude',
                                  values.locationFrom.original.longtitude
                                );
                                setFieldValue(
                                  'pickupDetails.location.comment',
                                  values.locationCommentFrom
                                );

                                setFieldValue(
                                  'deliveryDetails.location.name',
                                  values.location.original.name
                                );
                                setFieldValue(
                                  'deliveryDetails.location.latitude',
                                  values.location.original.latitude
                                );
                                setFieldValue(
                                  'deliveryDetails.location.longitude',
                                  values.location.original.longtitude
                                );
                                setFieldValue(
                                  'deliveryDetails.location.comment',
                                  values.locationComment
                                );
                              }
                            });
                          }
                          if (isValid) {
                            this.setState({
                              closeForm: true
                            });
                          }
                        }}
                        type="submit"
                        large
                      >
                        {t(
                          this.isAdditionalRequest() &&
                            this.isTransportation() &&
                            !isTransportationWindowOpen
                            ? 'Continue'
                            : this.isAdditionalRequest()
                            ? 'Submit'
                            : 'Save'
                        )}
                      </RaisedButton>
                      {!this.isAdditionalRequest() && (
                        <RaisedButton
                          type="submit"
                          disabled={isDisabled || isSubmitting}
                          large
                        >
                          {t('Save and create new')}
                        </RaisedButton>
                      )}
                    </div>
                  </PageActions>

                  <div className="service-request-edit--header">
                    <PageHeader
                      to={
                        isTransportationWindowOpen
                          ? ''
                          : this.getServiceRequestsGridUrl()
                      }
                      onClick={event => {
                        event.preventDefault();
                        if (isTransportationWindowOpen) {
                          this.setState({ isTransportationWindowOpen: false });
                        } else {
                          this.cancelForm(dirty, nav.serviceRequestNavigation);
                        }
                      }}
                      title={
                        isTransportationWindowOpen
                          ? t('Transportation details')
                          : this.isAdditionalRequest()
                          ? t('Create additional service request')
                          : t('Create service request')
                      }
                    />
                  </div>
                </>
              )}
            </ServiceRequestNavContext.Consumer>
            {isTransportationWindowOpen ? (
              <div className="transportation">
                <div className="transportation--columns">
                  <div>
                    {this.renderGeneralInformation()}
                    <Panel>
                      <div className="transportation--panel">
                        <H2>{t('Pickup details')}</H2>
                        <div className="transportation--panel-columns">
                          <div className="transportation--panel-columns-left">
                            <Field
                              component={Input}
                              placeholder={t('Place of loading')}
                              name="pickupDetails.location.name"
                            >
                              {t('Place of loading*')}
                            </Field>

                            <div className="transportation--fields-row">
                              <Field
                                component={Input}
                                placeholder={t('Latitude')}
                                name="pickupDetails.location.latitude"
                              >
                                {t('Latitude')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Longitude')}
                                name="pickupDetails.location.longitude"
                              >
                                {t('Longitude')}
                              </Field>
                            </div>
                            <div className="transportation--fields-row">
                              <Field
                                component={Input}
                                placeholder={t('Zip code')}
                                name="pickupDetails.location.zipCode"
                              >
                                {t('Zip code')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Post. add.')}
                                name="pickupDetails.location.postAddress"
                              >
                                {t('Post. add.')}
                              </Field>
                            </div>
                            <Field
                              component={Input}
                              placeholder={t('Location comment')}
                              name="pickupDetails.location.comment"
                            >
                              {t('Location comment')}
                            </Field>
                          </div>

                          <div className="transportation--panel-columns-right">
                            <Field
                              component={Input}
                              placeholder={t('Name POC')}
                              name="pickupDetails.namePoc"
                            >
                              {t('Name POC*')}
                            </Field>
                            <Field
                              component={Input}
                              placeholder={t('Mobile phone')}
                              name="pickupDetails.mobilePhone"
                            >
                              {t('Mobile phone*')}
                            </Field>
                            <Field
                              component={Input}
                              placeholder={t('Phone')}
                              name="pickupDetails.phone"
                            >
                              {t('Phone')}
                            </Field>
                            <Field
                              component={Input}
                              placeholder={t('E-mail')}
                              name="pickupDetails.email"
                            >
                              {t('E-mail*')}
                            </Field>
                          </div>
                        </div>

                        <div className="transportation--fields-row">
                          <Field
                            component={DateInput}
                            setFieldValue={setFieldValue}
                            name="pickupDetails.transportationTime.date"
                          >
                            {t('Earliest date')}
                          </Field>
                          <div className="transportation--fields-time">
                            <Field
                              className="transportation--field-time"
                              component={Input}
                              placeholder={t('HH')}
                              onKeyPress={this.timeCheck()}
                              onPaste={event => {
                                event.preventDefault();
                              }}
                              name="pickupDetails.transportationTime.hour"
                            >
                              {t('Time')}
                            </Field>
                            <Field
                              className="transportation--field-time"
                              component={Input}
                              placeholder={t('mm')}
                              onKeyPress={this.timeCheck(true)}
                              onPaste={event => {
                                event.preventDefault();
                              }}
                              name="pickupDetails.transportationTime.minute"
                            >
                              &nbsp;
                            </Field>
                          </div>
                        </div>

                        <div>
                          <Field
                            component={Input}
                            placeholder={t('Mission')}
                            name="pickupDetails.mission"
                          >
                            {t('Mission')}
                          </Field>
                        </div>
                      </div>
                    </Panel>
                  </div>

                  <div>
                    <Panel>
                      <div className="transportation--panel">
                        <H2>{t('Delivery details')}</H2>

                        <div className="transportation--panel-columns">
                          <div className="transportation--panel-columns-left">
                            <Field
                              component={Input}
                              placeholder={t('Place of delivery')}
                              name="deliveryDetails.location.name"
                            >
                              {t('Place of delivery*')}
                            </Field>

                            <div className="transportation--fields-row">
                              <Field
                                component={Input}
                                placeholder={t('Latitude')}
                                name="deliveryDetails.location.latitude"
                              >
                                {t('Latitude')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Longitude')}
                                name="deliveryDetails.location.longitude"
                              >
                                {t('Longitude')}
                              </Field>
                            </div>

                            <div className="transportation--fields-row">
                              <Field
                                component={Input}
                                placeholder={t('Zip code')}
                                name="deliveryDetails.location.zipCode"
                              >
                                {t('Zip code')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Post. add.')}
                                name="deliveryDetails.location.postAddress"
                              >
                                {t('Post. add.')}
                              </Field>
                            </div>
                            <Field
                              component={Input}
                              placeholder={t('Location comment')}
                              name="deliveryDetails.location.comment"
                            >
                              {t('Location comment')}
                            </Field>
                          </div>

                          <div className="transportation--panel-columns-right">
                            <Field
                              component={Input}
                              placeholder={t('Name POC')}
                              name="deliveryDetails.namePoc"
                            >
                              {t('Name POC*')}
                            </Field>
                            <Field
                              component={Input}
                              placeholder={t('Mobile phone')}
                              name="deliveryDetails.mobilePhone"
                            >
                              {t('Mobile phone*')}
                            </Field>
                            <Field
                              component={Input}
                              placeholder={t('Phone')}
                              name="deliveryDetails.phone"
                            >
                              {t('Phone')}
                            </Field>
                            <Field
                              component={Input}
                              placeholder={t('E-mail')}
                              name="deliveryDetails.email"
                            >
                              {t('E-mail*')}
                            </Field>
                          </div>
                        </div>

                        <div className="transportation--fields-row">
                          <Field
                            component={DateInput}
                            setFieldValue={setFieldValue}
                            name="deliveryDetails.transportationTime.date"
                          >
                            {t('Latest date')}
                          </Field>
                          <div className="transportation--fields-time">
                            <Field
                              className="transportation--field-time"
                              component={Input}
                              onKeyPress={this.timeCheck()}
                              onPaste={event => {
                                event.preventDefault();
                              }}
                              placeholder={t('HH')}
                              name="deliveryDetails.transportationTime.hour"
                            >
                              {t('Time')}
                            </Field>
                            <Field
                              className="transportation--field-time"
                              component={Input}
                              onKeyPress={this.timeCheck(true)}
                              onPaste={event => {
                                event.preventDefault();
                              }}
                              placeholder={t('mm')}
                              name="deliveryDetails.transportationTime.minute"
                            >
                              &nbsp;
                            </Field>
                          </div>
                        </div>
                        <div>
                          <Field
                            component={Textarea}
                            rows={6}
                            name="deliveryDetails.remarks"
                            placeholder={t('Remarks')}
                            label={t('Remarks')}
                          />
                        </div>

                        {this.renderTransportationInfo(values)}
                      </div>
                    </Panel>
                  </div>
                </div>
                <div>
                  <H2>{t('Detailed information')}</H2>
                  <div>
                    <FieldArray
                      name="lines"
                      render={arrayHelpers => {
                        return (
                          <>
                            <table className="table">
                              <thead>
                                {values.lines.length > 0 && (
                                  <tr>
                                    <th width={53}>{t('No.')}</th>
                                    <th width={76}>{t('Type')}</th>
                                    <th width={260}>
                                      {t('Description of goods/passenger')}
                                    </th>
                                    <th width={66}>{t('Q-ty')}</th>
                                    <th width={66}>{t('Length cm')}</th>
                                    <th width={66}>{t('Width cm')}</th>
                                    <th width={66}>{t('Height cm')}</th>
                                    <th width={66}>{t('Weight kg')}</th>
                                    <th width={127}>{t('DGR name')}</th>
                                    <th width={66}>{t('UN-NR')}</th>
                                    <th width={66}>{t('DGR class')}</th>
                                    <th width={66}>{t('NEQ kg')}</th>
                                    <th width={66}>{t('Remarks')}</th>
                                    <th></th>
                                  </tr>
                                )}
                              </thead>
                              <tbody>
                                {values.lines.map((item, index) => {
                                  const typeId = item.typeId
                                    ? item.typeId.toString()
                                    : '';

                                  return (
                                    <tr key={`key_${index}_tr`}>
                                      <td>{index + 1}</td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 76
                                          }}
                                          setFieldValue={setFieldValue}
                                          component={Dropdown}
                                          name={`lines[${index}].typeId`}
                                        >
                                          <option value="">
                                            {t('Not set')}
                                          </option>
                                          {forTransportationSubmitData.transportationDetailsTypes.map(
                                            item => {
                                              return (
                                                <option
                                                  key={ShortId.generate()}
                                                  value={item.id}
                                                >
                                                  {item.longName}
                                                </option>
                                              );
                                            }
                                          )}
                                        </Field>
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 260
                                          }}
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].description`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          setFieldValue={setFieldValue}
                                          component={NumericInput}
                                          name={`lines[${index}].quantity`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={typeId === '2'}
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].length`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={typeId === '2'}
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].width`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={typeId === '2'}
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].height`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={typeId === '2'}
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].weight`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={
                                            typeId === '2' || typeId === '3'
                                          }
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].dgrName`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={
                                            typeId === '2' || typeId === '3'
                                          }
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].unNr`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={
                                            typeId === '2' || typeId === '3'
                                          }
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].dgrClass`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          disabled={
                                            typeId === '2' || typeId === '3'
                                          }
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].neq`}
                                        />
                                      </td>
                                      <td>
                                        <Field
                                          noMargin
                                          style={{
                                            width: 66
                                          }}
                                          setFieldValue={setFieldValue}
                                          component={Input}
                                          name={`lines[${index}].remarks`}
                                        />
                                      </td>
                                      <td className="empty">
                                        <GhostButton
                                          className="transportation--remove-button"
                                          onClick={() => {
                                            Confirm(
                                              t('Delete'),
                                              t(
                                                '	Are you sure you want to delete this item?'
                                              ),
                                              () => {
                                                arrayHelpers.remove(index);
                                              }
                                            );
                                          }}
                                        >
                                          <Icon icon="delete" />
                                        </GhostButton>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <GhostButton
                              iconLeft={<Icon icon="Plus" />}
                              type="button"
                              onClick={() =>
                                arrayHelpers.push({
                                  typeId: undefined,
                                  description: undefined,
                                  quantity: undefined,
                                  length: undefined,
                                  width: undefined,
                                  height: undefined,
                                  weight: undefined,
                                  unNr: undefined,
                                  dgrName: undefined,
                                  dgrClass: undefined,
                                  neq: undefined,
                                  remarks: undefined
                                })
                              }
                            >
                              {t('Add new item')}
                            </GhostButton>
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="service-request">
                <Panel
                  withArrow
                  heading={{
                    title: t('Annex'),
                    number: 1
                  }}
                  actions={
                    <>
                      {isFirstPanelExpanded && (
                        <a href="/#" onClick={this.clearAnnexesTree} title="">
                          {t('Clear all')}
                        </a>
                      )}
                      <a
                        href="/#"
                        onClick={e => {
                          e.preventDefault();
                          this.toggleFirstPanelExpanded(!isFirstPanelExpanded);
                        }}
                      >
                        {isFirstPanelExpanded ? t('Collapse') : t('Expand')}
                      </a>
                    </>
                  }
                  className={
                    !isFirstPanelExpanded ? 'first-panel-collapsed' : ''
                  }
                >
                  <CheckboxTree
                    nodes={this.toAnnexesTreeEntity()}
                    checked={this.state.chTreeChecked}
                    expanded={this.state.chTreeExpanded}
                    onCheck={this.onAnnexCheck}
                    onExpand={expanded =>
                      this.setState({ chTreeExpanded: expanded })
                    }
                  />
                </Panel>
                <Panel
                  withArrow
                  heading={{
                    title: t('Items'),
                    number: 2
                  }}
                  className={
                    !isFirstPanelExpanded ? 'second-panel-expanded' : ''
                  }
                >
                  <div className="service-request--panel">
                    <Input
                      search="tiny"
                      icon={<Icon icon="search" />}
                      placeholder={t('Search items')}
                      onChange={this.handleQuickSearchChange}
                    />

                    <Grid
                      sortable={true}
                      pageable={{
                        buttonCount: 3,
                        pageSizes: false
                      }}
                      {...this.state.dataState}
                      {...this.props.oData}
                      data={this.props.oData.data.map(item => ({
                        ...item,
                        selected:
                          item.CatalogItemId === selectedCatalog.CatalogItemId
                      }))}
                      onDataStateChange={this.dataStateChange}
                      selectedField="selected"
                      onRowClick={({ dataItem }) =>
                        this.onGridRowClick(
                          dataItem,
                          setFieldValue,
                          validateForm
                        )
                      }
                    >
                      <GridColumn
                        field="Name"
                        title={'Name'}
                        cell={({ dataItem }) => {
                          return (
                            <td
                              onClick={() =>
                                this.onGridRowClick(
                                  dataItem,
                                  setFieldValue,
                                  validateForm
                                )
                              }
                            >
                              <Tooltip
                                content={dataItem.Name}
                                targetTagName="div"
                                className="tooltip"
                              >
                                {dataItem.Name}
                              </Tooltip>
                            </td>
                          );
                        }}
                      />
                      <GridColumn
                        field="UnitsOfMeasure"
                        title={'UoM'}
                        sortable={false}
                        cell={props => {
                          if (!props.dataItem.UnitsOfMeasure) {
                            return '';
                          }
                          const units = props.dataItem.UnitsOfMeasure.map(
                            unit => unit.UnitOfMeasureCode
                          );
                          return <td>{units.join(', ')}</td>;
                        }}
                      />
                      <GridColumn field="GroupName" title={'Group'} />
                      {this.props.isLoading && <GridLoader />}
                    </Grid>

                    {this.props.oData.data.length === 0 && (
                      <div className="service-request--custom-request">
                        <div className="service-request--custom-request-title">
                          {t('We cannot find this item in the Catalog')}
                        </div>
                        <div className="service-request--custom-request-button">
                          <GhostButton
                            iconLeft={<Icon icon="plus" />}
                            to={this.getCustomServiceRequestUrl()}
                          >
                            {t('Create custom service request')}
                          </GhostButton>
                        </div>
                      </div>
                    )}
                  </div>
                </Panel>
                <Panel
                  heading={{
                    title: t('Information'),
                    number: 3
                  }}
                >
                  <div className="service-request--panel">
                    <H5>{t('Item information')}</H5>
                    <InfoPanel
                      toolTipInformation={objectPath.get(
                        selectedCatalog,
                        'AnnexInformation',
                        ''
                      )}
                      key={objectPath.get(selectedCatalog, 'CatalogItemId')}
                      title={objectPath.get(selectedCatalog, 'Name', '-')}
                      code={objectPath.get(selectedCatalog, 'Code', '')}
                    >
                      {objectPath.get(selectedCatalog, 'Description', '-')}
                    </InfoPanel>

                    <div className="service-request--panel-info">
                      <div>
                        {t('Item price')}:{' '}
                        <span>
                          <Money
                            amount={objectPath.get(
                              selectedCatalog,
                              'RateFrom',
                              ''
                            )}
                          />{' '}
                          -{' '}
                          <Money
                            amount={objectPath.get(
                              selectedCatalog,
                              'RateTo',
                              ''
                            )}
                          />{' '}
                          {t(objectPath.get(selectedCatalog, 'Currency', ''))}{' '}
                          {defaultUom.UnitOfMeasureCode}
                        </span>
                      </div>

                      {objectPath
                        .get(selectedCatalog, 'CatalogAttachments', [])
                        .map(file => (
                          <File
                            key={ShortId.generate()}
                            name={file.Name}
                            attachmentId={file.AttachmentId}
                          />
                        ))}
                    </div>

                    <H5>{t('Delivery information')}</H5>
                    <DeliveryInformation
                      showTotalEstimates={values.quantity !== ''}
                      disabled={Object.keys(selectedCatalog).length === 0}
                      uoms={this.toUomsEntity(
                        selectedCatalog.UnitsOfMeasure || []
                      )}
                      activities={this.toActivityEntity(
                        this.props.forSubmitData.activities || []
                      )}
                      units={this.getUnits(this.props.forSubmitData.units)}
                      setFieldValue={setFieldValue}
                      results={this.props.calculateByDaysResults}
                      checked={values.isCalculatedByDays}
                      values={values}
                      activityInForm
                      showCurrency={true}
                      exerciseCurrency={this.getExerciseCurrency()}
                    />
                    {this.isTransportation() === false && (
                      <>
                        <H5>{t('Location details')}</H5>
                        <LocationDetails
                          setFieldValue={setFieldValue}
                          locationItems={this.toAutocompleteEntity(
                            this.props.query
                          )}
                          onQueryChange={this.onLocationQueryChange}
                          initialLocation={this.getInitialLocation(
                            values.location
                          )}
                          defaultQuery={
                            prevFormData.location
                              ? prevFormData.location.original.name
                              : values.location
                              ? values.location.original.name
                              : ''
                          }
                        />
                      </>
                    )}

                    {this.isTransportation() === true && (
                      <>
                        <H5>{t('Pickup location')}</H5>
                        <LocationDetails
                          setFieldValue={setFieldValue}
                          locationItems={this.toAutocompleteEntity(
                            this.props.query
                          )}
                          onQueryChange={this.onLocationQueryChange}
                          isExpandable={true}
                          schema={{
                            location: 'locationFrom',
                            locationComment: 'locationCommentFrom'
                          }}
                          initialLocation={this.getInitialLocation(
                            values.locationFrom
                          )}
                          defaultQuery={
                            prevFormData.locationFrom
                              ? prevFormData.locationFrom.original.name
                              : values.locationFrom
                              ? values.locationFrom.original.name
                              : ''
                          }
                        />
                        <H5>{t('Delivery location')}</H5>
                        <LocationDetails
                          setFieldValue={setFieldValue}
                          locationItems={this.toAutocompleteEntity(
                            this.props.query
                          )}
                          onQueryChange={this.onLocationQueryChange}
                          isExpandable={true}
                          initialLocation={this.getInitialLocation(
                            values.location
                          )}
                          defaultQuery={
                            prevFormData.location
                              ? prevFormData.location.original.name
                              : values.location
                              ? values.location.original.name
                              : ''
                          }
                        />
                      </>
                    )}

                    <H5>{t('Additional details')}</H5>
                    <Field
                      name="comment"
                      setFieldValue={setFieldValue}
                      placeholder={t('Describe additional details')}
                      label={t('Comment')}
                      component={Textarea}
                    />

                    <div className="documents-section">
                      <DropZone
                        value={this.props.attachments}
                        onSelect={files => {
                          this.handleFileUpload(files);
                        }}
                        onRemove={file => {
                          this.props.deleteAttachment(file);
                        }}
                      />
                    </div>
                  </div>
                </Panel>
              </div>
            )}
          </form>
        )}
      </Formik>
    );
  }
}

ServiceRequest.propTypes = {
  getAnnexesTree: PropTypes.func,
  isLoading: PropTypes.bool,
  oData: PropTypes.object,
  query: PropTypes.array,
  t: PropTypes.func,
  annexesTree: PropTypes.array,
  queryOData: PropTypes.func,
  queryLocations: PropTypes.func,
  resetOData: PropTypes.func,
  forSubmit: PropTypes.func,
  forSubmitData: PropTypes.object,
  calculateByDays: PropTypes.func,
  clearCalculateByDays: PropTypes.func,
  calculateByDaysResults: PropTypes.object,
  match: PropTypes.object,
  create: PropTypes.func,
  history: PropTypes.object,
  uploadAttachments: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  attachments: PropTypes.array,
  createAdditionalServiceRequest: PropTypes.func,
  forTransportationSubmit: PropTypes.object
};

export default ServiceRequest;
